import { createAnalyticsEvent } from './ga-helper';
import { handleAgendaLink } from './agenda-link-helper';

function listenClickInsideContainer(
  containerEl,
  setPromotedPage,
  gtag = null,
  setInternalLink = null
) {
  if (!containerEl) {
    return;
  }

  // Walk up the DOM from `start` to the nearest ancestor that has
  // the given attribute. Returns null if none match. This handles
  // the common case where the user clicks an inner span / text node
  // of a button rather than the button itself.
  const findWithAttr = (start, attr) => {
    if (!start) return null;
    if (typeof start.closest === 'function') {
      try {
        return start.closest('[' + attr + ']');
      } catch (e) {
        /* fall through */
      }
    }
    let el = start;
    while (el && el !== containerEl && el.nodeType === 1) {
      if (el.hasAttribute && el.hasAttribute(attr)) return el;
      el = el.parentElement || el.parentNode;
    }
    return null;
  };

  const findHrefHost = (start) => {
    if (!start) return null;
    if (typeof start.closest === 'function') {
      try {
        return start.closest('a[href], [href]');
      } catch (e) {
        /* fall through */
      }
    }
    let el = start;
    while (el && el !== containerEl && el.nodeType === 1) {
      if (el.getAttribute && el.getAttribute('href')) return el;
      el = el.parentElement || el.parentNode;
    }
    return null;
  };

  const listener = function (evt) {
    var rawTarget = evt.target || evt.srcElement;

    // Google Analytics Event
    const gaEvent = {
      action: undefined,
      category: undefined,
      label: undefined
    };

    // LINKS — climb to the nearest ancestor anchor / [href] so a
    // click on an inner <span> or text inside the link still works.
    const linkEl = findHrefHost(rawTarget);
    if (linkEl) {
      const href = linkEl.getAttribute('href');
      const parts = href.split('/');

      if (parts[0] === 'internal') {
        evt.stopImmediatePropagation();
        evt.preventDefault();
        if (handleAgendaLink(parts)) {
          return;
        }
        if (parts[1] === 'infoPage') {
          setPromotedPage({
            page: 'info',
            id: parts[2]
          });
          return;
        }

        if (parts[1] === 'speaker') {
          setPromotedPage({
            page: 'speaker',
            id: parts[2]
          });
          return;
        }

        if (parts[1] === 'session') {
          let liveUrl;
          if (parts.length > 3) {
            const protocolIndex = parts.findIndex((part) =>
              part.toLowerCase().startsWith('http')
            );
            liveUrl = parts.slice(protocolIndex).join('/');
          }

          if (setInternalLink) {
            setInternalLink({
              type: parts[1],
              id: parts[2],
              date: parts[3],
              liveUrl: liveUrl
            });
            return;
          }
        }

        if (setInternalLink) {
          setInternalLink({ type: parts[1], id: parts[2] });
        }
      }
      // A poll / quiz / profile / sponsor / agenda... button can itself be an
      // <a href="#">; don't bail out before the data-tappin-* handling below.
      const dataButton = [
        'data-tappin-poll-id',
        'data-tappin-infopage-id',
        'data-tappin-session-id',
        'data-tappin-agenda-session',
        'data-tappin-userprofile',
        'data-tappin-participants',
        'data-tappin-sponsor-id',
        'data-tappin-documents',
        'data-tappin-speaker-id'
      ].some((attr) => findWithAttr(rawTarget, attr));
      if (!dataButton) return;
    }

    // ── Data-attribute buttons. Each branch climbs to the nearest
    //    ancestor that actually carries the attribute, so a click on
    //    an inner <i> / <span> / text node still triggers it. ─────
    let element = findWithAttr(rawTarget, 'data-tappin-infopage-id');
    if (element) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const elInfoPageId = element.getAttribute('data-tappin-infopage-id');
      const parts = elInfoPageId.split('/');
      if (parts[0] === 'internal') {
        if (setInternalLink) {
          setInternalLink({ type: parts[1], id: parts[2] });
        }
      } else {
        setPromotedPage({
          page: 'info',
          id: elInfoPageId
        });
      }

      gaEvent.action = 'ClickOnInfoPage';
      gaEvent.category = 'PromotedMessage';
      gaEvent.label = elInfoPageId;
    }

    element = findWithAttr(rawTarget, 'data-tappin-session-id');
    if (element) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const elSessionId = element.getAttribute('data-tappin-session-id');
      setPromotedPage({
        page: 'session',
        id: elSessionId,
        liveUrl: element.getAttribute('data-tappin-session-liveurl')
      });

      gaEvent.action = 'ClickOnSession';
      gaEvent.category = 'PromotedMessage';
      gaEvent.label = elSessionId;
    }

    element = findWithAttr(rawTarget, 'data-tappin-agenda-session');
    if (element) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const value = element.getAttribute('data-tappin-agenda-session');
      if (handleAgendaLink(value.split('/'))) {
        return;
      }
    }

    element = findWithAttr(rawTarget, 'data-tappin-userprofile');
    if (element) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const value = element.getAttribute('data-tappin-userprofile');
      const parts = value.split('/');
      if (setInternalLink) {
        setInternalLink({ type: parts[1] });
      }
    }

    element = findWithAttr(rawTarget, 'data-tappin-participants');
    if (element) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const value = element.getAttribute('data-tappin-participants');
      const parts = value.split('/');
      if (setInternalLink) {
        setInternalLink({ type: parts[1] });
      }
    }

    element = findWithAttr(rawTarget, 'data-tappin-sponsor-id');
    if (element) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const elSponsorId = element.getAttribute('data-tappin-sponsor-id');
      const parts = elSponsorId.split('/');
      if (parts[0] === 'internal') {
        if (setInternalLink) {
          setInternalLink({ type: parts[1], id: parts[2] });
        }
      } else {
        setPromotedPage({
          page: 'sponsor',
          id: elSponsorId
        });
      }

      gaEvent.action = 'ClickOnSponsor';
      gaEvent.category = 'PromotedMessage';
      gaEvent.label = elSponsorId;
    }

    element = findWithAttr(rawTarget, 'data-tappin-documents');
    if (element) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const value = element.getAttribute('data-tappin-documents');
      const parts = value.split('/');
      if (setInternalLink) {
        setInternalLink({ type: parts[1] });
      }
    }

    element = findWithAttr(rawTarget, 'data-tappin-speaker-id');
    if (element) {
      evt.preventDefault();
      evt.stopPropagation();
      const elSpeakerId = element.getAttribute('data-tappin-speaker-id');
      const parts = elSpeakerId.split('/');
      if (parts[0] === 'internal') {
        if (setInternalLink) {
          setInternalLink({ type: parts[1], id: parts[2] });
        }
      } else {
        setPromotedPage({
          page: 'speaker',
          id: elSpeakerId
        });
      }

      gaEvent.action = 'ClickOnSpeaker';
      gaEvent.category = 'PromotedMessage';
      gaEvent.label = elSpeakerId;
    }

    const pollEl = findWithAttr(rawTarget, 'data-tappin-poll-id');
    if (!pollEl) {
      if (gtag && gaEvent.action) {
        createAnalyticsEvent(
          gtag,
          gaEvent.action,
          gaEvent.category,
          gaEvent.label
        );
      }
      return;
    }
    evt.preventDefault();
    evt.stopPropagation();
    setPromotedPage({
      page: pollEl.getAttribute('data-tappin-poll-type') || 'poll',
      id: pollEl.getAttribute('data-tappin-poll-id')
    });

    if (gtag) {
      createAnalyticsEvent(
        gtag,
        'ClickOnPoll',
        'PromotedMessage',
        pollEl.getAttribute('data-tappin-poll-id')
      );
    }
  };

  containerEl.addEventListener('click', listener);

  return listener;
}

function removeClickInsideContainerListener(containerEl, listener) {
  if (!containerEl) {
    return;
  }

  containerEl.removeEventListener('click', listener);
}

export { listenClickInsideContainer, removeClickInsideContainerListener };
